import { useState } from 'react';

import {
  Icons,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
} from '@commenda/kakashi';

import { ChatIconButton } from './ChatIconButton';
import { type PanelHistoryConversation } from './conversation-history.helper';
import { ConversationHistoryList } from './ConversationHistoryList';

/**
 * Past conversations, reachable without leaving the page.
 *
 * Takes its list rather than fetching one, because each surface scopes history differently: the
 * chat panel shows the workspace's, the workflow builder shows this template's. The list itself is
 * `ConversationHistoryList`, the same one a side panel shows.
 */
export const PanelHistoryMenu = ({
  conversations,
  activeConversationId,
  onSelect,
  viewerId,
  isLoading = false,
  hasError = false,
}: {
  conversations: readonly PanelHistoryConversation[];
  activeConversationId: string | null;
  onSelect: (conversationId: string) => void;
  /** Splits the viewer's own conversations from ones shared with them. Omit on a surface with no
   *  sharing, and the list is grouped by recency alone. */
  viewerId?: string;
  isLoading?: boolean;
  hasError?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <Tooltip content="Chat history" asChild>
        <PopoverTrigger asChild>
          <ChatIconButton
            aria-label="Chat history"
            // The design shows the trigger filled while its menu is open.
            isActive={isOpen}
            icon={<Icons.ClockRewind className="size-4" />}
          />
        </PopoverTrigger>
      </Tooltip>
      <PopoverContent
        align="end"
        sideOffset={4}
        // Shadow is the menu's own, softer than any shared elevation token.
        className="flex max-h-[425px] w-[339px] flex-col overflow-hidden rounded-xl border-1/2 border-border-secondary p-none shadow-[0px_2px_8px_rgba(0,0,0,0.04)]"
      >
        <ConversationHistoryList
          conversations={conversations}
          activeConversationId={activeConversationId}
          onSelect={(conversationId) => {
            onSelect(conversationId);
            setIsOpen(false);
          }}
          {...(viewerId ? { viewerId } : {})}
          isLoading={isLoading}
          hasError={hasError}
        />
      </PopoverContent>
    </Popover>
  );
};
